/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import PreSubscribed from '@cont/PreSubscribed/PreSubscribed';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import useSubscribed from '@hooks/useSubscribed';
import {
  fetchTopologyInfo,
  fetchPreSubscribedStaticContent
} from '@actions/topologies';
import { fetchPlusHomeFeed } from '@actions/plusCourses';
import { fetchPresubscribedBlocks } from '@actions/goal';
import { fetchPartPaymentStatus } from '@actions/checkout';
import {
  fetchIconicSubscriptionInfo,
  fetchPriceHikeInfo,
  fetchFaqs
} from '@actions/others';
import {
  setSelectedNav,
  fetchFeedbackOptions,
  fetchRenewalTouchPoint,
  setSelectedGoal
} from '@actions/auth';
import pushRoute from '@utils/pushRoute';
import SEO from '@seo/index';
import goalSchema from '@seo/goal/goal';
import Dynamic from '@base/Dynamic/Dynamic';
import metaInfo from '@seo/goal/metaInfo';

const PlusHome = Dynamic(() => import('@cont/PlusHome/PlusHome'));

const PlusHomePage = ({ goalInfo, goalUID, querySlug }) => {
  const isMobile = useSelector(({ helper }) => helper.isMobile);
  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn || false);
  const isSubscribed = useSubscribed(goalUID);
  const preSubscribedBlocks = useSelectorToStore(
    ({ paginator }) => paginator.preSubscribedBlocks[goalUID]
  );

  const checkPartPaymentStatus = useSelector(
    ({ auth }) => auth.checkPartPaymentStatus[goalUID]
  );

  const faqs = useSelector(({ others }) => others.faqs);

  const { goalSlug, title, uid, ogImage } = goalInfo || {};

  useEffect(() => {
    if (querySlug !== goalSlug)
      pushRoute('/goal/[goalSlug]/[goalUID]', `/goal/${goalSlug}/${uid}/`, {
        shallow: true
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = useDispatchToStore();

  useEffect(() => {
    if (isLoggedIn && isSubscribed) {
      dispatch(fetchPlusHomeFeed(goalUID));
      dispatch(setSelectedNav('plus'));
      dispatch(fetchFeedbackOptions(goalUID));
      dispatch(fetchRenewalTouchPoint(goalUID));
    } else {
      dispatch(fetchPreSubscribedStaticContent(goalUID));
    }
  }, [isLoggedIn, isSubscribed, goalUID, dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchPresubscribedBlocks(goalUID, 20, 0));
      if (checkPartPaymentStatus) {
        dispatch(fetchPartPaymentStatus(goalUID));
      }
    }
  }, [isLoggedIn, dispatch, goalUID, checkPartPaymentStatus]);
  const metainfo = metaInfo[goalUID] || {};
  const goalTitle = metainfo.title || `${title} | Unacademy`;
  const goalDescription =
    metainfo.description ||
    `Prepare live from Top Educators with comprehensive, structured and crash courses along with practice section for ${title}  on Unacademy Plus.`;
  const url = `https://unacademy.com/goal/${goalSlug}/${uid}/`;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';

  return (
    <>
      <SEO
        title={goalTitle}
        url={url}
        description={goalDescription}
        openGraph={{
          title: goalTitle,
          description: goalDescription,
          images: [
            {
              url: imageURL
            }
          ],
          url
        }}
        schema={goalSchema(
          faqs,
          title,
          url,
          preSubscribedBlocks?.results || [],
          goalDescription
        )}
      />
      {isLoggedIn && isSubscribed && !isMobile ? (
        <PlusHome />
      ) : (
        <PreSubscribed
          preSubscribedBlocks={preSubscribedBlocks.results}
          isSubscribed={isSubscribed}
        />
      )}
    </>
  );
};

PlusHomePage.pageInfo = {
  name: ({
    router: {
      query: { goalUID = '' }
    },
    state
  }) => {
    const plusSubscriptionUids = state.auth.me?.plusSubscriptionUids || [];
    const isSubscribed = plusSubscriptionUids.includes(goalUID.toUpperCase());
    return isSubscribed ? 'Goal Home' : 'Free Home Page';
  },
  layoutOptions: {
    showPlusNav: true
  }
};

export default PlusHomePage;

PlusHomePage.getInitialProps = async ({ store, query, isServer, res }) => {
  let count = 5;
  const isGamma = process.env.ENVIRONMENT === 'gamma';
  if (isGamma) count = 3;
  const { goalUID, goalSlug: querySlug } = query;
  const { auth, helper } = store.getState();
  const { isLoggedIn, me, checkPartPaymentStatus } = auth || {};
  let storeState = store.getState();
  if (!storeState.topologies?.data[goalUID]?.selfDataFetched)
    await store.dispatch(fetchTopologyInfo(goalUID));
  storeState = store.getState();
  const goal = storeState.topologies?.data[goalUID?.toUpperCase()];
  store.dispatch(setSelectedNav('plus'));
  if (!goal?.isPlusAvailable) {
    if (isServer) {
      res.writeHead(302, {
        Location: `/goal/${goal.goalSlug}/${goalUID}/free-platform`
      });
      return res.end();
    }
    return pushRoute(
      '/goal/[goalSlug]/[goalUID]/free-platform',
      `/goal/${goal.goalSlug}/${goalUID}/free-platform`
    );
  }
  if (helper.isMobile) {
    store.dispatch(setSelectedGoal(goal));
  }
  if (isLoggedIn && checkPartPaymentStatus[goalUID]) {
    await store.dispatch(fetchPartPaymentStatus(goalUID));
  }

  const isSubscribed = me?.plusSubscriptionUids?.includes(
    goalUID.toUpperCase()
  );

  const topology = store.getState().topologies?.data?.[goalUID];
  const isIconicAvailable = topology?.isIconicAvailable;

  if (
    isIconicAvailable &&
    !store.getState().others?.iconicSubscriptionInfo?.[`iconic-${goalUID}`]
  ) {
    await store.dispatch(fetchIconicSubscriptionInfo(`iconic-${goalUID}`));
  }

  if (!isLoggedIn || !isSubscribed) {
    await store.dispatch(fetchPriceHikeInfo());
  }

  storeState = store.getState();
  const promises = [];
  if (isLoggedIn && isSubscribed && !helper.isMobile) {
    if (!storeState.auth.renewalData?.[goalUID])
      promises.push(store.dispatch(fetchRenewalTouchPoint(goalUID)));
    if (!storeState.paginator?.plusHomePageFeed?.[goalUID])
      promises.push(store.dispatch(fetchPlusHomeFeed(goalUID)));
    if (!storeState.users?.feedbackOptions)
      promises.push(store.dispatch(fetchFeedbackOptions(goalUID)));
  } else {
    if (!storeState.paginator?.preSubscribedBlocks?.[goalUID]) {
      promises.push(
        store.dispatch(fetchPresubscribedBlocks(goalUID, count, 0))
      );
      promises.push(store.dispatch(fetchFaqs()));
    }
    if (
      !storeState.topologies.preSubscribedStaticContent?.[goalUID] &&
      // eslint-disable-next-line no-underscore-dangle
      !storeState.topologies.preSubscribedStaticContent?.__default
    ) {
      promises.push(store.dispatch(fetchPreSubscribedStaticContent(goalUID)));
    }
  }
  if (promises.length) await Promise.all(promises);

  return {
    goalUID,
    isServer,
    goalInfo: store.getState().topologies.data[goalUID],
    querySlug
  };
};

PlusHomePage.propTypes = {
  goalInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired,
  querySlug: PropTypes.string.isRequired
};
